import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Save } from "lucide-react";
import { API, FALLBACK_TAX_SEASON } from "@/lib/content";
import { Eyebrow } from "@/components/Reveal";

export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("fa_admin_key") || "");
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem("fa_admin_key"));
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("season");
  const [messages, setMessages] = useState([]);
  const [articles, setArticles] = useState([]);
  const [ts, setTs] = useState(FALLBACK_TAX_SEASON);
  const [editArticle, setEditArticle] = useState(null);

  useEffect(() => {
    if (!authed) return;
    const headers = { "X-Admin-Key": adminKey };
    axios.get(`${API}/content`).then((r) => {
      if (r.data.tax_season) setTs(r.data.tax_season);
      setArticles(r.data.articles || []);
    });
    axios.get(`${API}/admin/messages`, { headers }).then((r) => setMessages(r.data.messages || []));
  }, [authed, adminKey]);

  const login = (e) => {
    e.preventDefault();
    axios
      .get(`${API}/admin/messages`, { headers: { "X-Admin-Key": password } })
      .then(() => {
        sessionStorage.setItem("fa_admin_key", password);
        setAdminKey(password);
        setAuthed(true);
        toast.success("Signed in.");
      })
      .catch(() => toast.error("Incorrect password."));
  };

  const saveSeason = async () => {
    try {
      const { data } = await axios.put(`${API}/admin/tax-season`, ts, { headers: { "X-Admin-Key": adminKey } });
      setTs(data.tax_season);
      toast.success("Tax Season Centre updated.");
    } catch {
      toast.error("Save failed.");
    }
  };

  const saveArticle = async () => {
    try {
      await axios.put(`${API}/admin/articles/${editArticle.slug}`, editArticle, { headers: { "X-Admin-Key": adminKey } });
      toast.success("Article updated.");
    } catch {
      toast.error("Save failed.");
    }
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#102036] px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm rounded-lg bg-white p-10">
          <Lock className="h-8 w-8 text-[#102036]" />
          <h1 className="mt-4 font-display text-3xl font-semibold text-[#102036]">Admin</h1>
          <p className="mt-1 text-sm text-[#4A5568]">Firm staff only.</p>
          <label htmlFor="admin-password" className="mb-2 mt-6 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Password</label>
          <input id="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" className="field-input" autoFocus />
          <button type="submit" data-testid="admin-login-button" className="mt-6 w-full rounded-full bg-[#102036] py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white hover:bg-[#102036]">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF] pt-24">
      <div className="mx-auto max-w-5xl px-5 pb-24 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <Eyebrow>Ford &amp; Associates — internal</Eyebrow>
            <h1 className="mt-2 font-display text-4xl font-semibold text-[#102036]">Content editor</h1>
          </div>
          <button
            onClick={() => { sessionStorage.removeItem("fa_admin_key"); setAuthed(false); }}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#102036]/20 px-6 py-2.5 text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036] hover:border-[#102036]"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        <div className="mt-8 flex gap-2" role="tablist">
          {[["season", "Tax Season"], ["articles", "Tax Insights"], ["messages", "Messages"]].map(([id, label]) => (
            <button
              key={id}
              role="tab"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              data-testid={`admin-tab-${id}`}
              className={`rounded-full px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.14em] transition-colors ${tab === id ? "bg-[#102036] text-white" : "border border-[#102036]/15 text-[#102036]/70 hover:text-[#102036]"}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "season" && (
          <div className="mt-8 rounded-lg border border-[#102036]/10 bg-white p-8" data-testid="admin-tax-season-editor">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Status label</label>
                <input className="field-input" maxLength={60} value={ts.status} onChange={(e) => setTs({ ...ts, status: e.target.value })} data-testid="admin-season-status" />
              </div>
              <div>
                <label className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Status tone</label>
                <select className="field-input" value={ts.status_tone} onChange={(e) => setTs({ ...ts, status_tone: e.target.value })} data-testid="admin-season-tone">
                  <option value="open">Open (green)</option>
                  <option value="notice">Notice (amber)</option>
                  <option value="closed">Closed (grey)</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Headline</label>
                <input className="field-input" maxLength={400} value={ts.headline} onChange={(e) => setTs({ ...ts, headline: e.target.value })} data-testid="admin-season-headline" />
              </div>
            </div>

            <p className="mt-8 text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Key dates</p>
            {(ts.dates || []).map((d, i) => (
              <div key={i} className="mt-3 flex gap-3">
                <input className="field-input flex-1" placeholder="Label" value={d[0]} onChange={(e) => setTs({ ...ts, dates: ts.dates.map((x, j) => (j === i ? [e.target.value, x[1]] : x)) })} data-testid={`admin-season-date-label-${i}`} />
                <input className="field-input w-56" placeholder="Value" value={d[1]} onChange={(e) => setTs({ ...ts, dates: ts.dates.map((x, j) => (j === i ? [x[0], e.target.value] : x)) })} data-testid={`admin-season-date-value-${i}`} />
                <button onClick={() => setTs({ ...ts, dates: ts.dates.filter((_, j) => j !== i) })} aria-label="Remove date" className="rounded-md border border-[#102036]/15 px-4 text-lg text-[#102036]/50 hover:border-red-300 hover:text-red-500">×</button>
              </div>
            ))}
            <button onClick={() => setTs({ ...ts, dates: [...(ts.dates || []), ["", ""]] })} data-testid="admin-add-date-button" className="mt-3 rounded-full border border-[#102036]/30 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#102036]">+ Add date</button>

            <p className="mt-8 text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Notices</p>
            {(ts.notices || []).map((n, i) => (
              <div key={i} className="mt-3 flex gap-3">
                <input className="field-input flex-1" value={n} onChange={(e) => setTs({ ...ts, notices: ts.notices.map((x, j) => (j === i ? e.target.value : x)) })} data-testid={`admin-notice-${i}`} />
                <button onClick={() => setTs({ ...ts, notices: ts.notices.filter((_, j) => j !== i) })} aria-label="Remove notice" className="rounded-md border border-[#102036]/15 px-4 text-lg text-[#102036]/50 hover:border-red-300 hover:text-red-500">×</button>
              </div>
            ))}
            <button onClick={() => setTs({ ...ts, notices: [...(ts.notices || []), ""] })} data-testid="admin-add-notice-button" className="mt-3 rounded-full border border-[#102036]/30 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#102036]">+ Add notice</button>

            <div className="mt-8">
              <label className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Special instructions</label>
              <textarea rows={4} className="field-input" maxLength={2000} value={ts.instructions || ""} onChange={(e) => setTs({ ...ts, instructions: e.target.value })} data-testid="admin-season-instructions" />
            </div>

            <button onClick={saveSeason} data-testid="admin-save-season-button" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#102036] px-8 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white hover:bg-[#102036]">
              <Save className="h-4 w-4" /> Save tax season
            </button>
          </div>
        )}

        {tab === "articles" && (
          <div className="mt-8 rounded-lg border border-[#102036]/10 bg-white p-8" data-testid="admin-articles-editor">
            <div className="flex flex-wrap gap-2">
              {articles.map((a) => (
                <button
                  key={a.slug}
                  onClick={async () => {
                    try {
                      const { data } = await axios.get(`${API}/articles/${a.slug}`);
                      setEditArticle({ ...data.article, body: (data.article.body || []).join("\n") });
                    } catch {
                      toast.error("Could not load article.");
                    }
                  }}
                  data-testid={`admin-article-select-${a.slug}`}
                  className={`rounded-full px-5 py-2.5 text-[12px] font-bold transition-colors ${editArticle?.slug === a.slug ? "bg-[#102036] text-white" : "border border-[#102036]/15 text-[#102036]/70"}`}
                >
                  {a.category} — {a.date_display || a.title.slice(0, 24)}
                </button>
              ))}
            </div>
            {editArticle && (
              <div className="mt-8 space-y-5">
                <div>
                  <label className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Title</label>
                  <input className="field-input" maxLength={200} value={editArticle.title} onChange={(e) => setEditArticle({ ...editArticle, title: e.target.value })} data-testid="admin-article-title" />
                </div>
                <div>
                  <label className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Excerpt</label>
                  <textarea rows={3} className="field-input" maxLength={600} value={editArticle.excerpt || ""} onChange={(e) => setEditArticle({ ...editArticle, excerpt: e.target.value })} data-testid="admin-article-excerpt" />
                </div>
                <div>
                  <label className="mb-2 block text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036]/70">Article body (one paragraph per line)</label>
                  <textarea
                    rows={12}
                    className="field-input font-mono text-[13px]"
                    value={editArticle.body ?? ""}
                    onChange={(e) => setEditArticle({ ...editArticle, body: e.target.value })}
                    data-testid="admin-article-body"
                  />
                </div>
                <button onClick={saveArticle} data-testid="admin-save-article-button" className="inline-flex items-center gap-2 rounded-full bg-[#102036] px-8 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white hover:bg-[#3682AE]">
                  <Save className="h-4 w-4" /> Save article
                </button>
              </div>
            )}
          </div>
        )}

        {tab === "messages" && (
          <div className="mt-8 space-y-4" data-testid="admin-messages-list">
            {messages.length === 0 && <p className="rounded-lg border border-[#102036]/10 bg-white p-8 text-sm text-[#4A5568]">No messages yet.</p>}
            {messages.map((m) => (
              <div key={m.id} className="rounded-lg border border-[#102036]/10 bg-white p-6" data-testid="admin-message-card">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-bold text-[#102036]">{m.name} <span className="ml-2 rounded-full bg-[#102036]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#102036]">{m.service}</span></p>
                  <p className="text-xs text-[#4A5568]/70">{(m.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
                <p className="mt-2 text-sm text-[#4A5568]">{m.email} {m.phone ? `· ${m.phone}` : ""} {m.company ? `· ${m.company}` : ""}</p>
                <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-[#102036]/85">{m.message}</p>
                {m.emailed && <p className="mt-3 mr-4 inline text-[11px] font-bold uppercase tracking-[0.14em] text-[#3682AE]">✉ Emailed to office</p>}
                {m.autoreplied && <p className="mt-3 inline text-[11px] font-bold uppercase tracking-[0.14em] text-[#3682AE]">✓ Confirmation sent to client</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
